/** @file Live store: link status, who is online, own pending batches, notices and the commit feed. */
import type { RenderEntry } from '@pose2d/api/render/renderEntry';
import type { CommitSummary } from '@pose2d/api/types';
import type { CommandCall, Pose2DError } from '@pose2d/format/types';
import { createStore } from 'zustand/vanilla';
import type { PendingCommand } from './types';

/** `offline` while the socket is down; `catchingUp` / `resyncing` while the sync loop reads the server. */
export type LiveStatus = 'offline' | 'connecting' | 'live' | 'catchingUp' | 'resyncing';

/** One dispatched batch = one commit = one undo step; its commands are also in `DocSlice.pending`. */
export interface PendingBatch {
  /** Client sequence number within the open project (1, 2, …). */
  seq: number;
  /** The commands as they are sent, with the ids their optimistic apply minted. */
  pending: PendingCommand[];
  /** The rev of the commit that carries this batch, once known (E8-R3). */
  committedRev: number | null;
  /** True once its REST request has left. */
  sent: boolean;
}

/** A refused batch (`calls` for Retry), a refused undo/redo/revert or a server error frame (`calls` null). */
export interface LiveNotice {
  id: number;
  calls: CommandCall[] | null;
  error: Pose2DError;
}

/** Everything live sync shows; written only by `api/sync` and `api/history`. */
export interface LiveState {
  status: LiveStatus;
  /** Actor name → entity name being edited (null: online, editing nothing); the own actor included. */
  actors: Record<string, string | null>;
  batches: PendingBatch[];
  notices: LiveNotice[];
  /** Commit summaries seen since the project opened (plus loaded pages), oldest first. */
  commits: CommitSummary[];
  /** Bumped by every whole-document reload, after the reloaded slice is written, so views holding derived data reload. */
  epoch: number;
  /** The project's render feed (CR-013), ascending seq. */
  renders: RenderEntry[];
  /** The last renders page said older entries exist. */
  rendersMore: boolean;
}

/** Vanilla store; startLiveSync resets it for every project. */
export const liveStore = createStore<LiveState>()(() => ({
  status: 'offline', actors: {}, batches: [], notices: [], commits: [], epoch: 0, renders: [], rendersMore: false,
}));
